import { StudentCoinBalanceWidget } from 'src/components/widgets/StudentCoinBalanceWidget';
import {
    dateFormat,
    dateFormatWithTime,
    displayFormat,
} from 'src/constants/constants';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { useGetAcademicLeaveByIdQuery } from 'src/api/endpoints/academic';
import React, {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { formatMaterialName } from 'src/api/endpoints/materials';
import { getUserFullname } from 'src/utils/getFullname';
import { ResponseError, useGetShortStatisticsQuery } from 'src/api';
import { Link, useParams } from 'react-router-dom';
import { getTime } from 'src/utils/date/getTime';
import { dayWorker } from 'src/utils/dateWorker';
import { Avatar } from 'src/components/Avatar';
import { MenuItem } from 'primereact/menuitem';
import { classNames } from 'primereact/utils';
import { Helmet } from 'react-helmet-async';
import LegacyLoader from 'src/components/Loader';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { filter, map, sumBy } from 'lodash';
import './module.scss';
import {
    ContinueAcademicVacationModal,
    CreateAcademicVacationModal,
    ExpelStudentModal,
    OutOfAcademicVacationModal,
} from 'src/components/AcademicVacationModals';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { StudentModalContent__Legacy } from 'src/pages/edu/Students';
import {
    UpdateStudentAccountArg,
    useGetStudentByIdQuery,
    useUpdateStudentAccountMutation,
} from 'src/api/endpoints/students';
import { notifyAboutSuccess } from 'src/utils/notify';
import { skipToken } from '@reduxjs/toolkit/query';
import {
    DebtStatus,
    StudentDebt,
} from 'src/pages/employees/BranchStudentDebtsPage';
import { Loader } from 'src/components/shared/loader';
import { useGetBranchStudentDebtsQuery } from 'src/api/endpoints/students-debts';
import { Col, Row } from 'antd';

export const StudentPage: FC<AuthorizedUserProps> = (props: AuthorizedUserProps): ReactElement => {
    const studentId: number = Number(useParams().studentId);

    const { data: student, isLoading: isStudentLoading } =
        useGetStudentByIdQuery({
            params: {
                branchId: props.currentBranchId,
                studentId: studentId,
            },
        });

    const { data: studentDebts, isLoading: isStudentDebtsLoading } =
        useGetBranchStudentDebtsQuery(
            student
                ? {
                      params: {
                          branchId: props.currentBranchId,
                          studentId: student.id,
                      },
                  }
                : skipToken
        );

    const [skipAcademicDataFetch, setSkipAcademicDataFetch] = useState(true);

    const { isLoading, data } = useGetShortStatisticsQuery(
        student && !isStudentLoading
            ? {
                  userId: student.userId,
              }
            : skipToken
    );

    const { isLoading: isAcademicLoading, data: academicData } =
        useGetAcademicLeaveByIdQuery(
            student && !isStudentLoading && !skipAcademicDataFetch
                ? student.id
                : skipToken
        );

    const menuRef = useRef<Menu>(null);

    const [outOfAcademicModalVisible, setOutOfAcademicModalVisible] =
        useState(false);

    const [continueAcademicModalVisible, setContinueOfAcademicModalVisible] =
        useState(false);

    const [expelStudentModalVisible, setExpelStudentModalVisible] =
        useState(false);

    const [createAcademicModalVisible, setCreateAcademicModalVisible] =
        useState(false);

    const [isEditModalVisible, setEditModalVisible] = useState(false);

    const getActions = (
        isRegistered: boolean,
        status:
            | 'Действующий'
            | 'В академическом отпуске'
            | 'Отчислен'
            | 'Выпущен'
    ): MenuItem[] => {
        const editAction: MenuItem = {
            label: 'Изменить',
            command: () => setEditModalVisible(true),
        };

        if (!isRegistered) {
            if (status === 'Действующий') {
                return [
                    editAction,
                    {
                        label: 'Отчислить',
                        command: () => setExpelStudentModalVisible(true),
                    },
                ];
            }

            return [];
        }

        if (status === 'Действующий') {
            return [
                editAction,
                {
                    label: 'Отправить в академ. отпуск',
                    command: () => setCreateAcademicModalVisible(true),
                },
                {
                    label: 'Отчислить',
                    command: () => setExpelStudentModalVisible(true),
                },
            ];
        }

        if (status === 'В академическом отпуске') {
            return [
                editAction,
                {
                    label: 'Вывести',
                    command: () => setOutOfAcademicModalVisible(true),
                },
                {
                    label: 'Продлить',
                    command: () => setContinueOfAcademicModalVisible(true),
                },
                {
                    label: 'Отчислить',
                    command: () => setExpelStudentModalVisible(true),
                },
            ];
        }

        return [];
    };

    useEffect(() => {
        if (
            data?.profile.id &&
            data.profile.status.name === 'В академическом отпуске'
        ) {
            setSkipAcademicDataFetch(false);
        }
    }, [data]);

    const actions: MenuItem[] = useMemo(() => {
        if (!data) return [];

        return getActions(
            data.profile.registered,
            data.profile.status.name as
                | 'Действующий'
                | 'В академическом отпуске'
                | 'Отчислен'
                | 'Выпущен'
        );
    }, [data]);

    const [updateStudent, { isLoading: isUpdateStudentLoading }] =
        useUpdateStudentAccountMutation();

    const onStudentUpdate = async (v: UpdateStudentAccountArg) => {
        await updateStudent(v)
            .unwrap()
            .then(() => {
                notifyAboutSuccess({
                    content: 'Студент успешно изменен',
                });

                setEditModalVisible(false);
            })
            .catch((reason: ResponseError) => {
                notifyAboutSuccess({ content: reason.message, sound: true });
            });
    };

    return (
        <StudentLayout auth={props}>
            {!!student && (
                <Helmet
                    title={`${getUserFullname(student)} · ${props.currentBranch.name} · Личный кабинет`}
                />
            )}

            {isLoading || !data || isAcademicLoading ? (
                <LegacyLoader />
            ) : (
                <>
                    <div className="grid">
                        <div className="flex flex-column lg:col-4 col-12 card py-0">
                            <div className="header flex align-items-center justify-content-between points-bg">
                                <div className="title">Студент</div>
                            </div>
                            <div className="content flex-1">
                                <div
                                    className="area"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <div
                                        className={'student-profile'}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            flexDirection: 'column',
                                            gap: '10px',
                                        }}
                                    >
                                        <Avatar
                                            size={'124px'}
                                            isLoading={isLoading}
                                            userId={data?.profile?.user?.id}
                                        />

                                        <div
                                            className={
                                                'flex flex-column gap-2 text-center align-items-center'
                                            }
                                        >
                                            <div className="title points-text">
                                                {getUserFullname({
                                                    name: data.profile.user
                                                        .firstname,
                                                    surname:
                                                        data.profile.user
                                                            .lastname,
                                                    patronymic:
                                                        data.profile.user
                                                            .patronymic,
                                                })}
                                            </div>
                                            <div>
                                                <Link
                                                    to={`/b/${props.currentBranchId}/groups/${data.profile.group.id}`}
                                                >
                                                    <p
                                                        style={{
                                                            color: '#80888f',
                                                        }}
                                                    >
                                                        {
                                                            data.profile.group
                                                                .name
                                                        }
                                                    </p>
                                                </Link>
                                            </div>
                                            <div>
                                                Статус:{' '}
                                                {data?.profile.status.name}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="student-footer"
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <div className="w-full flex flex-row justify-content-between flex-wrap">
                                            <StudentCoinBalanceWidget
                                                studentId={data.profile.id}
                                            />

                                            {!!actions.length && (
                                                <>
                                                    <Menu
                                                        model={actions}
                                                        popup
                                                        ref={menuRef}
                                                        id="popup_menu_left"
                                                    />
                                                    <Button
                                                        size="small"
                                                        label="Действия"
                                                        icon="pi pi-chevron-down"
                                                        iconPos="right"
                                                        onClick={event => {
                                                            if (
                                                                menuRef.current
                                                            ) {
                                                                menuRef.current.toggle(
                                                                    event
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-4 col-12 card py-0 md-mt-24">
                            <div className="header flex align-items-center justify-content-between points-bg">
                                <div className="title">Успеваемость</div>
                            </div>
                            <div className="content">
                                <div
                                    className="area bg"
                                    style={{ height: '137.117px' }}
                                >
                                    <div className="title points-text">
                                        Средний балл
                                    </div>
                                    <div className="flex align-items-end text">
                                        <div className="points-text h1 border">
                                            {
                                                data?.performance.marks.average
                                                    .current
                                            }
                                        </div>
                                        {map(
                                            data?.performance.marks.average
                                                .previous,
                                            (point, idx) => (
                                                <div
                                                    className={`h${idx + 2 > 3 ? 3 : idx + 2} border`}
                                                    key={point}
                                                >
                                                    {Number.isInteger(point)
                                                        ? point
                                                        : point.toFixed(1)}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                                <div className="grid">
                                    {data?.performance.marks.last ? (
                                        data?.performance.marks.last.map(
                                            (point, idx) => (
                                                <div
                                                    className="col-4 py-0 xs-col-6"
                                                    id={classNames({
                                                        first: idx <= 1,
                                                    })}
                                                    key={
                                                        point.date + point.mark
                                                    }
                                                >
                                                    <div
                                                        className="area bg"
                                                        style={{
                                                            height: 152.233,
                                                        }}
                                                    >
                                                        <div className="title">
                                                            Получено
                                                        </div>
                                                        <div className="h1 dark">
                                                            {point.mark}
                                                        </div>
                                                        <small className="date">
                                                            {dayWorker(
                                                                point.date
                                                            ).format(
                                                                dateFormat
                                                            )}
                                                        </small>
                                                    </div>
                                                </div>
                                            )
                                        )
                                    ) : (
                                        <div
                                            className="col-4 py-0 xs-col-6"
                                            id="first"
                                        >
                                            <div
                                                className="area bg"
                                                style={{ height: 152.233 }}
                                            >
                                                <div className="title">
                                                    Пока нет оценок
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-4 col-12 card py-0 md-mt-24">
                            <div className="header flex align-items-center justify-content-between homework-bg">
                                <div className="title">Домашнее задание</div>
                            </div>
                            <div className="content">
                                <div className="grid mt-0 h-full">
                                    <div
                                        className={
                                            (data?.performance.homework
                                                .currentTask
                                                ? 'col-7'
                                                : 'col-12') +
                                            ' xs-col-12 py-0 relative'
                                        }
                                    >
                                        <div className="grid mt-0">
                                            <div className="col-6 py-0">
                                                <div
                                                    className="area bg"
                                                    style={{
                                                        height: 137.117,
                                                    }}
                                                >
                                                    <div className="title homework-text">
                                                        Всего
                                                        <br />
                                                    </div>
                                                    <div className="h1 dark">
                                                        {
                                                            data?.performance
                                                                .homework.total
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 py-0">
                                                <div
                                                    className="area bg"
                                                    style={{
                                                        height: 137.117,
                                                    }}
                                                >
                                                    <div className="title">
                                                        Активные
                                                    </div>
                                                    <div className="h1 dark">
                                                        {
                                                            data?.performance
                                                                .homework
                                                                .upcoming
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid">
                                            <div className="col-6 py-0">
                                                <div
                                                    className="area bg"
                                                    style={{
                                                        height: 152.233,
                                                    }}
                                                >
                                                    <div className="title">
                                                        Просрочено
                                                    </div>
                                                    <div className="h1 dark">
                                                        {
                                                            data?.performance
                                                                .homework
                                                                .expired
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 py-0">
                                                <div
                                                    className="area bg"
                                                    style={{
                                                        height: 152.233,
                                                    }}
                                                >
                                                    <div className="title">
                                                        Сдано
                                                    </div>
                                                    <div className="h1 dark">
                                                        {
                                                            data?.performance
                                                                .homework
                                                                .completed
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {data?.performance.homework.currentTask ? (
                                        <div className="col-5 xs-col-12 py-0">
                                            <div className="area overflow-y-scroll flex flex-column justify-content-between h-full">
                                                <div>
                                                    <div className="title light sm-mt-24">
                                                        Ближайшее
                                                    </div>
                                                    <div className="text bold fz-20">
                                                        {
                                                            data?.performance
                                                                .homework
                                                                .currentTask
                                                                .name
                                                        }
                                                    </div>
                                                    <small className="text">
                                                        {
                                                            data?.performance
                                                                .homework
                                                                .currentTask
                                                                .description
                                                        }
                                                    </small>
                                                </div>
                                                <small className="text date">
                                                    До{' '}
                                                    {dayWorker(
                                                        data?.performance
                                                            .homework
                                                            .currentTask
                                                            .expiryAt
                                                    ).format(
                                                        dateFormatWithTime
                                                    )}
                                                </small>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="lg:col-4 col-12 card py-0">
                            <div className="header flex align-items-center justify-content-between materials-bg">
                                <div className="title">Учебные материалы</div>
                            </div>
                            <div className="content" style={{ height: 305.35 }}>
                                {data?.performance.material ? (
                                    <div className="area flex flex-column justify-content-between h-full overflow-y-scroll">
                                        <div>
                                            <div className="flex justify-content-between">
                                                <div className="title materials-text">
                                                    Последнее поступление:
                                                </div>
                                            </div>
                                            <small className="date">
                                                {dayWorker(
                                                    data.performance.material
                                                        .createdAt
                                                ).format(displayFormat)}
                                            </small>
                                            <div className="text fz-20 mt-6">
                                                {
                                                    data.performance.material
                                                        .subject.name
                                                }
                                            </div>
                                            <div className="text fz-20 bold mt-24">
                                                {formatMaterialName(
                                                    data.performance.material
                                                )}
                                            </div>
                                        </div>
                                        <small className="date mt-24">
                                            {'Урок'}
                                        </small>
                                    </div>
                                ) : (
                                    <div className="text fz-20">
                                        Здесь пока ничего нет
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="lg:col-4 col-12 card py-0 md-mt-24">
                            <div className="header flex align-items-center justify-content-between timetable-bg">
                                <div className="title">Расписание</div>
                            </div>
                            <div className="content" style={{ height: 305.35 }}>
                                {data?.performance.lesson ? (
                                    <div className="area overflow-y-scroll max-h-full">
                                        <div className="title timetable-text">
                                            Следующее занятие:
                                        </div>
                                        <div className="text fz-20 bold mt-6">
                                            {dayWorker(
                                                data?.performance.lesson.time
                                            ).format(dateFormatWithTime)}
                                            <br />
                                            {getTime(
                                                data?.performance.lesson.time
                                            )}
                                        </div>
                                        <small className="date mt-24">
                                            Тема занятия:
                                        </small>
                                        <div className="text fz-20 bold mt-6">
                                            {data?.performance.lesson.theme}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text fz-20">
                                        Здесь пока ничего нет
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="lg:col-4 col-12 card py-0 md-mt-24 payment-card">
                            <div className="header flex align-items-center justify-content-between payment-bg">
                                <div className="title">Посещаемость</div>
                                <Link
                                    to={`/b/${props.currentBranchId}/students/${student?.id}/attendance`}
                                >
                                    <Button
                                        icon="pi pi-chevron-right white"
                                        className="p-button-rounded p-button-outlined"
                                    />
                                </Link>
                            </div>
                            <div
                                className="content overflow-y-scroll max-h-full"
                                style={{ height: 305.35 }}
                            >
                                {data?.attendance ? (
                                    <>
                                        <div className="area bg">
                                            <div className="title">
                                                Пар было
                                            </div>
                                            <div className="h1 dark">
                                                {data.attendance.total}
                                            </div>
                                            <div className="title mt-2">
                                                Присутствовал на
                                            </div>
                                            <div className="h1 dark">
                                                {data.attendance.attended +
                                                    data.attendance.late}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text fz-20">
                                        Здесь пока ничего нет
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <Row
                        style={{ width: '100%', marginBottom: '12px' }}
                        gutter={{
                            lg: 12,
                        }}
                    >
                        <Col xs={24} lg={8}>
                            <DebtsBlock
                                debts={studentDebts}
                                isLoading={isStudentDebtsLoading}
                            />
                        </Col>
                    </Row>

                    {academicData?.id ? (
                        <>
                            <OutOfAcademicVacationModal
                                visible={outOfAcademicModalVisible}
                                branchId={props.currentBranchId}
                                onHide={() =>
                                    setOutOfAcademicModalVisible(false)
                                }
                                studentName={`${data?.profile.user.firstname} ${data?.profile.user.lastname} ${data?.profile.user.patronymic}`}
                                academicId={academicData.id}
                            />
                            <ContinueAcademicVacationModal
                                visible={continueAcademicModalVisible}
                                onHide={() =>
                                    setContinueOfAcademicModalVisible(false)
                                }
                                studentName={`${data?.profile.user.firstname} ${data?.profile.user.lastname} ${data?.profile.user.patronymic}`}
                                academicId={academicData.id}
                            />
                        </>
                    ) : null}
                    {data?.profile.id ? (
                        <>
                            <ExpelStudentModal
                                visible={expelStudentModalVisible}
                                onHide={(): void =>
                                    setExpelStudentModalVisible(false)
                                }
                                studentName={
                                    data.profile.registered
                                        ? `${data?.profile.user.firstname} ${data?.profile.user.lastname} ${data?.profile.user.patronymic}`
                                        : data.profile.contract
                                }
                                studentId={data.profile.id}
                            />
                            <CreateAcademicVacationModal
                                branchId={props.currentBranchId}
                                studentId={data.profile.id}
                                visible={createAcademicModalVisible}
                                onHide={(): void =>
                                    setCreateAcademicModalVisible(false)
                                }
                            />
                            <LegacyModal
                                header={'Изменение студента'}
                                visible={isEditModalVisible}
                                onHide={(): void => {
                                    setEditModalVisible(false);
                                }}
                            >
                                <StudentModalContent__Legacy
                                    initialState={{
                                        studentId: data.profile.user.id,
                                        contract: data.profile.contract,
                                        curGroup: data.profile.group.id,
                                    }}
                                    branchId={props.currentBranchId}
                                    onSubmit={onStudentUpdate}
                                    update={true}
                                    loading={isUpdateStudentLoading}
                                />
                            </LegacyModal>
                        </>
                    ) : null}
                </>
            )}
        </StudentLayout>
    );
};

export type DebtsBlockProps = {
    debts?: StudentDebt[];
    isLoading?: boolean;
};

export const DebtsBlock = (props: DebtsBlockProps) => {
    const { debts = [], isLoading = false } = props;

    const activeDebts = useMemo(() => {
        const activeDebts: StudentDebt[] = filter(
            debts,
            (d: StudentDebt): boolean => d.status === DebtStatus.Active
        );

        return {
            amount: sumBy(activeDebts, d => Number(d.amount)),
            quantity: activeDebts.length,
            elements: activeDebts,
        };
    }, [debts]);

    return (
        <div className={'debts-block'} id={'debts'}>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <div className="block-header">
                        <div className="block-title">
                            <div className="block-icon">
                                <CreditCardIcon
                                    color={'#70747F'}
                                    size={'25px'}
                                />
                            </div>
                            <div className="block-name">Задолженности</div>
                        </div>
                        <div className="block-actions">
                            <Link to={'debts'}>
                                <div className="block-action-button">
                                    <OpenInNewIcon
                                        size={'25px'}
                                        color={'#70747F'}
                                    />
                                </div>
                            </Link>
                        </div>
                    </div>
                    <div className="block-content">
                        {activeDebts.quantity > 0 && (
                            <p className={'debt-amount'}>
                                {activeDebts.amount.toLocaleString('ru')}
                                <span className={'currency'}>{' ₽'}</span>
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export type IconProps = {
    size?: string | number;
    color?: string;
};

export const CreditCardIcon: FC<IconProps> = (
    props: IconProps
): ReactElement => {
    const { size = '16px', color = '#000000' } = props;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height={size}
            width={size}
            viewBox="0 -960 960 960"
            fill={color}
        >
            <path d="M340.62-140q-84.08 0-142.35-58.27Q140-256.54 140-340.62q0-35.3 12.04-68.61t34.5-60.38l141.23-169.85L264-767.77q-9.23-18.07 1.12-35.15Q275.46-820 296.16-820h367.68q20.7 0 31.04 17.08 10.35 17.08 1.12 35.15l-63.77 128.31 141.23 169.85q22.46 27.07 34.5 60.38T820-340.62q0 84.08-58.58 142.35Q702.85-140 619.38-140H340.62ZM480-330.39q-29.92 0-51.11-21.19-21.2-21.19-21.2-51.11 0-29.93 21.2-51.12Q450.08-475 480-475q29.92 0 51.11 21.19 21.2 21.19 21.2 51.12 0 29.92-21.2 51.11-21.19 21.19-51.11 21.19ZM382.69-665h195L625-760H335l47.69 95Zm-42.07 465h278.76q58.93 0 99.77-41.04Q760-282.08 760-340.62q0-24.77-8.5-48.03-8.5-23.27-24.27-42.04L582.15-605h-203.3l-145.7 173.69q-15.77 18.77-24.46 42.35-8.69 23.57-8.69 48.34 0 58.54 41.04 99.58T340.62-200Z" />
        </svg>
    );
};

export const OpenInNewIcon = (props: IconProps) => {
    const { size = '16px', color = '#000000' } = props;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height={size}
            width={size}
            viewBox="0 -960 960 960"
            fill={color}
        >
            <path d="M228.31-164q-27.01 0-45.66-18.65Q164-201.3 164-228.31v-503.38q0-27.01 18.65-45.66Q201.3-796 228.31-796h210.3q11.07 0 18.54 7.41 7.46 7.4 7.46 18.38 0 10.98-7.46 18.59-7.47 7.62-18.54 7.62h-210.3q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h503.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-210.3q0-11.07 7.4-18.54 7.41-7.46 18.39-7.46t18.59 7.46q7.62 7.47 7.62 18.54v210.3q0 27.01-18.65 45.66Q758.7-164 731.69-164H228.31ZM744-706.85 405.85-368.69q-8.31 8.3-17.89 8-9.57-.31-18.27-9-8.69-8.7-8.69-18.58 0-9.88 8.69-18.58L706.85-744H602q-11.07 0-18.53-7.4-7.47-7.41-7.47-18.39t7.47-18.59Q590.93-796 602-796h161.84q13.77 0 22.96 9.2 9.2 9.19 9.2 22.96V-602q0 11.07-7.41 18.53-7.4 7.47-18.38 7.47-10.98 0-18.59-7.47Q744-590.93 744-602v-104.85Z" />
        </svg>
    );
};
